import React, { useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const FLOW_LABELS = { processing: "Processing", dispatched: "Dispatched", delivered: "Delivered", ready_for_pickup: "Ready for Pickup", collected: "Collected" };
export const flowFor = (o) => ((o.delivery || {}).method === "pickup" ? ["processing", "ready_for_pickup", "collected"] : ["processing", "dispatched", "delivered"]);

export function FulfillmentEditor({ order, onUpdated }) {
  const flow = flowFor(order);
  const [status, setStatus] = useState(order.fulfillment_status || flow[0]);
  const [courier, setCourier] = useState(order.courier || "");
  const [tracking, setTracking] = useState(order.tracking || "");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const paid = ["paid", "completed"].includes((order.payment_status || "").toLowerCase());

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.patch(`/admin/orders/${order.id}/fulfillment`, { status, courier, tracking, note, notify });
      onUpdated(data.order);
      setNote("");
      toast.success(data.emailed ? "Status updated — customer emailed" : notify ? "Status updated (no email sent — customer email missing or Resend domain unverified)" : "Status updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  if (!paid) return <p className="text-xs text-muted-foreground pt-2" data-testid="fulfillment-unpaid">Delivery status can be set once the order is paid.</p>;
  return (
    <div className="pt-3 space-y-2 border-t border-brand/10" data-testid="fulfillment-editor">
      <div className="text-muted-foreground">Delivery status</div>
      <div className="flex flex-wrap gap-2">
        {flow.map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`fulfillment-status-${s}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${status === s ? "bg-brand text-white border-brand" : "border-brand/20 hover:border-brand"}`}>{FLOW_LABELS[s]}</button>
        ))}
      </div>
      {status === "dispatched" && (
        <div className="grid grid-cols-2 gap-2">
          <Input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Courier (optional)" data-testid="fulfillment-courier-input" />
          <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking no. (optional)" data-testid="fulfillment-tracking-input" />
        </div>
      )}
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to customer (optional)" data-testid="fulfillment-note-input" />
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="fulfillment-notify-checkbox" /> Email the customer {order.email ? `(${order.email})` : "(no email on order)"}</label>
      <Button onClick={save} disabled={busy} className="w-full bg-brand hover:bg-brand-600" data-testid="fulfillment-save-btn">{busy ? "Saving…" : "Update status"}</Button>
      {!!(order.fulfillment_history || []).length && (
        <ul className="text-xs text-muted-foreground space-y-1 pt-1" data-testid="fulfillment-history">
          {[...order.fulfillment_history].reverse().map((h) => (
            <li key={h.ts}><b className="text-foreground">{FLOW_LABELS[h.status]}</b> · {new Date(h.ts).toLocaleString()}{h.note ? ` · ${h.note}` : ""}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
